import { useEffect, useState, useContext } from 'react';
import { UserContext } from './UserContext';
import { canManageUser, canonicalRole, isAdminType } from './utils/roleUtils.js'
import Sidebar from './Sidebar.js';
import { Card, Image, Row, Col, Container, Form, Button, Modal } from 'react-bootstrap';
import Breadcrumb from './Breadcrumb.js';
import { Link } from 'react-router-dom';
import { useNavigate, useParams } from 'react-router-dom';
import UserAddGeneral from './UserAddGeneral.js';
import UserAddAccess from './UserAddAccess.js';
import api from './api/axiosInstance.js';
import NavTabs from './NavTabs.js';
import icSucess from './assets/img/ic-celebration-circle.svg';
import { TicketCountContext } from './TicketCountContext';


    const UserEditAccess = () => {
        const { id } = useParams();
        const navigate = useNavigate();
        // Form fields state
        const [errors, setErrors] = useState({});
        const [profileImage, setProfileImage] = useState(null);
        const [companies, setCompanies] = useState([]);
        const [activityStatus, setActivityStatus] = useState(true);
        const [showSavedModal, setShowSavedModal] = useState(false);
        const [showErrorMessage, setShowErrorMessage] = useState(false);
        const [savedModalTitle, setSavedModalTitle] = useState('Update successful');
        const [savedModalMessage, setSavedModalMessage] = useState('');
        const [showDeleteModal, setShowDeleteModal] = useState(false);
        const [showDeactivateModal, setShowDeactivateModal] = useState(false);
        const { ticketCount } = useContext(TicketCountContext);

        const [generalData, setGeneralData] = useState({
            profileImage: '',
            firstName: '',
            familyName: '',
            gender: '',
            company: '',
            companyRole: '',
            activityStatus: false,
        })

        const { user: actor } = useContext(UserContext);

        const actorRole = canonicalRole(actor?.role);
        const actorId = String(actor?._id || actor?.id || '');

        const [isLoadingUser, setIsLoadingUser] = useState(true);

        const [accessData, setAccessData] = useState({ role: '' });
        const [loadedAccessRole, setLoadedAccessRole] = useState(''); // original role for permission checks

        const isSelfEdit = actorId && actorId === String(id);

        const targetRole = canonicalRole(accessData?.role);
        const originalTargetRole = canonicalRole(loadedAccessRole || targetRole);

        const isActorReady = !!actorId && !!actorRole;

        // ✅ Gate 1: can user open the editor UI?
        const canOpenAccessEditor =
            !isSelfEdit &&
            !isLoadingUser &&
            isActorReady &&
            (actorRole === 'superadmin' || actorRole === 'titanAdmin');

        // ✅ Gate 2: can user actually change/save access for this target?
        // superadmin: can change anyone (except self)
        // titanAdmin: can change anyone EXCEPT superadmin (except self)
        const canChangeAccess =
            !isSelfEdit &&
            !isLoadingUser &&
            isActorReady &&
            originalTargetRole !== 'superadmin' &&
            canManageUser(actorRole, originalTargetRole);

        const userNavItems = [
            { to: '/user-edit/:id', eventKey: 'userInfo', label: 'About' },
            { to: '/user-edit-academics/:id', eventKey: 'academicsInfo', label: 'Academics' },
            { to: '/user-edit-work-preferences/:id', eventKey: 'work', label: 'Work Preferences' },
            { to: '/user-edit-access/:id', eventKey: 'access', label: 'Access & Permissions' },
            { to: '/user-edit-history/:id', eventKey: 'history', label: 'History of changes' },
        ];

        const normalizeId = (x) => {
            if (!x) return '';
            if (typeof x === 'string') return x;
            if (typeof x === 'object') {
                if (x._id) return normalizeId(x._id);
                if (x.$oid) return String(x.$oid);
                if (x.id) return normalizeId(x.id);
                if (typeof x.toHexString === 'function') return x.toHexString();
                if (typeof x.toString === 'function' && x.toString !== Object.prototype.toString) {
                    const s = x.toString();
                    if (/^[a-fA-F0-9]{24}$/.test(s)) return s;
                }
            }
            return '';
        };

        useEffect(() => {
            // Fetch user data for editing
            const fetchUserData = async () => {

                setIsLoadingUser(true);

                try {
                    const res = await api.get(`/api/users/${id}`);
                    const currentUser = res.data;

                    // setActivityStatus(companyData.activityStatus ?? false);

                    if (!currentUser) {
                        alert('User not found.');
                        setIsLoadingUser(false); // ✅
                        return;
                    }

                    // Set general data
                    setGeneralData({
                        profileImage: currentUser.profileImage || '',
                        firstName: currentUser.firstName || '',
                        familyName: currentUser.familyName || '',
                        gender: currentUser.gender || '',
                        profileImagePreview: currentUser.profileImage || '',
                        company: normalizeId(currentUser.company),
                        companyRole: currentUser.companyRole || '',
                        activityStatus: currentUser.activityStatus || false,
                    });

                    // Set Access data
                    setAccessData({ role: canonicalRole(currentUser.role || '') });
                    setLoadedAccessRole(canonicalRole(currentUser.role || ''));
                    setIsLoadingUser(false);
                                            

                } catch (error) {
                    console.error('Error fetching user data:', error);
                    alert('An error occurred while fetching user details.');
                    setIsLoadingUser(false);
                }
            };

            fetchUserData();
        }, [id]);

        const validateGeneralFields = () => {
            const newErrors = {};

            if (!generalData.firstName.trim()) newErrors.firstName = 'First name is required';
            if (!generalData.familyName.trim()) newErrors.familyName = 'Last name is required';
            if (!generalData.gender) newErrors.gender = 'Gender is required';
            

            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
        };

        const handleGeneralSubmit = async (e) => {
            if (e) e.preventDefault();
            if (!validateGeneralFields()) return false; // ✅ Return false if validation fails

            try {
                const data = new FormData();

                data.append('firstName', generalData.firstName || '');
                data.append('familyName', generalData.familyName || '');
                data.append('gender', generalData.gender || '');

                if (profileImage) {
                    data.append('profileImage', profileImage);
                }

                await api.put(`/api/users/${id}`, data);

                setSavedModalMessage('User General information updated successfully.');
                setShowSavedModal(true);
                return true; // ✅ Return true on success
            } catch (error) {
                console.error('Error updating general information:', error);
                setShowErrorMessage(true);
                return false; // ✅ Return false on failure
            }
        };

        const handleGeneralChange = (e) => {
            const { name, value, files } = e.target;

            if (name === 'profileImage' && files && files.length > 0) {
                const file = files[0];
                const fileUrl = URL.createObjectURL(file);
                setProfileImage(file);
                setGeneralData((prevData) => ({
                    ...prevData,
                    profileImagePreview: fileUrl, // ✅ only preview, not actual file
                }));
                return;
            }

            setGeneralData((prevData) => ({
                ...prevData,
                [name]: value,
            }));

            setErrors((prevErrors) => ({
                ...prevErrors,
                [name]: '',
            }));
        };

        const handleGeneralReset = (resetData) => {
            setGeneralData(resetData);
            setErrors({});
        };

        const isActive = generalData.activityStatus;

        const handleDeactivate = async () => {
            try {
                const endpoint = isActive ? `/api/users/${id}/deactivate` : `/api/users/${id}/activate`;
                await api.put(endpoint);
                setShowDeactivateModal(false);
                setGeneralData((prev) => ({ ...prev, activityStatus: !isActive }));
            } catch (err) {
                console.error('Error toggling account status:', err);
            }
        };

        const handleDelete = async () => {
            try {
                await api.delete(`/api/users/${id}`);
                setShowDeleteModal(false);
                navigate('/onboarding-user-list');
            } catch (err) {
                console.error('Error deleting account:', err);
            }
        };

        const handleAccessChange = (e) => {
            const { name, value } = e.target;

            setAccessData((prevData) => ({
                ...prevData,
                [name]: value,
            }));

            setErrors((prevErrors) => ({
                ...prevErrors,
                [name]: '',
            }));
        };

        const handleAccessSubmit = async (e) => {
            if (e) e.preventDefault();

            try {
                await api.put(`/api/users/${id}/access`, {
                    role: accessData.role || '',
                });

                setSavedModalMessage('User Access & permissions information updated successfully.');
                setShowSavedModal(true);
                setShowErrorMessage(false);
                return true;
            } catch (err) {
                console.error('❌ Failed to update access info:', err);
                setShowErrorMessage(true);
                return false;
            }
        };


        return (
            <>
                <Sidebar ticketCount={ticketCount} />
                <main className="main-content flex-grow-1">
                    <Container>
                        <Row>
                            <Col>
                                <Breadcrumb />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Card>
                                    <Card.Body className="pt-0">
                                        <Row>
                                            <Col xs={12}>
                                                <>
                                                    <Row>
                                                        <Col>
                                                            <Form onSubmit={handleGeneralSubmit}>
                                                                <UserAddGeneral
                                                                    formData={{ 
                                                                        ...generalData
                                                                    }}
                                                                    profileImage={generalData.profileImagePreview}
                                                                    handleChange={handleGeneralChange}
                                                                    handleGeneralSubmit={handleGeneralSubmit}
                                                                    errors={errors}
                                                                    isEditPage={true}
                                                                    activityStatus={activityStatus}
                                                                    handleReset={handleGeneralReset}
                                                                    companies={companies}
                                                                />
                                                            </Form>
                                                        </Col>
                                                    </Row>
                                                    <Row className="mb-5">
                                                        <Col>
                                                            <NavTabs items={userNavItems} />
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>
                                                            <Form onSubmit={handleAccessSubmit}>
                                                                <UserAddAccess
                                                                    formData={accessData}
                                                                    handleChange={handleAccessChange}
                                                                    handleAccessSubmit={handleAccessSubmit}
                                                                    errors={errors}
                                                                    isEditPage={true}
                                                                    isDisabled={!canChangeAccess}
                                                                    isEditDisabled={!canOpenAccessEditor}
                                                                    isFieldsDisabled={!canChangeAccess}
                                                                    actorRole={actorRole}
                                                                />
                                                            </Form>
                                                        </Col>
                                                    </Row>
                                                </>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                        <Row className="buttons-row">
                            <Col>
                                <Link to="/user-add" className="btn btn-outline-primary">
                                    Add new user
                                </Link>
                                <Button variant="outline-secondary" onClick={() => setShowDeactivateModal(true)}>{isActive ? 'Deactivate account' : 'Activate account'}</Button>
                                <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>Delete account</Button>
                            </Col>
                        </Row>
                    </Container>
                </main>

                <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{isActive ? 'Deactivate account' : 'Activate account'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>{isActive
                            ? 'Are you sure you want to deactivate this account?'
                            : 'Are you sure you want to reactivate this account?'
                        }</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary me-3" onClick={() => setShowDeactivateModal(false)}>Cancel</Button>
                        <Button variant="primary" onClick={handleDeactivate}>{isActive ? 'Deactivate' : 'Activate'}</Button>
                    </Modal.Footer>
                </Modal>

                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Delete account</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to permanently delete this account? This action cannot be undone.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary me-3" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                        <Button variant="danger" onClick={handleDelete}>Delete account</Button>
                    </Modal.Footer>
                </Modal>

                {/* Saved info Modal */}
                <Modal show={showSavedModal} onHide={() => setShowSavedModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title></Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Image
                            src={icSucess}
                            alt=""
                            className="d-block m-auto mb-4"
                        />
                        <h2 className="text-center mb-3">{savedModalTitle}</h2>
                        <p className="text-center">{savedModalMessage}</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={() => setShowSavedModal(false)}>OK</Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    };

    export default UserEditAccess;
